import React, { useState } from 'react';
import SEO from '../components/SEO';
import { company } from '../config/company';
import s from '../styles/legalStyles';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Data-deletion request page (GDPR "right to erasure").
 * Posts to /api/leads with source=data-deletion so it lands in the CRM.
 */
const DataDeletion = () => {
  const [email, setEmail] = useState('');
  const [details, setDetails] = useState('');
  const [status, setStatus] = useState('idle'); // idle | working | success | error

  const submit = async (e) => {
    e.preventDefault();
    setStatus('working');
    try {
      const res = await fetch(`${API}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: 'Verzoek gegevensverwijdering',
          last_name: '',
          email: email.trim(),
          phone: '',
          message: `[DATA DELETION REQUEST]\n\n${details.trim() || 'Verzoek tot verwijdering van alle persoonsgegevens.'}`,
          source: 'data-deletion',
          cta_type: 'data-deletion',
        }),
      });
      if (!res.ok) throw new Error('submit-failed');
      setStatus('success');
      setEmail('');
      setDetails('');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div style={s.page}>
      <SEO
        title="Żądanie usunięcia danych"
        description={`Poproś o usunięcie swoich danych osobowych w ${company.brand}. Rozpatrujemy żądanie w ciągu 30 dni zgodnie z RODO.`}
        noindex
      />
      <div style={s.container}>
        <h1 style={s.title}>Żądanie usunięcia danych</h1>
        <p style={s.lastUpdated}>
          Ostatnia aktualizacja: {company.effectiveDate}
        </p>

        <article style={s.article}>
          <p>
            Zgodnie z RODO masz prawo zażądać usunięcia swoich danych osobowych.
            {' '}{company.brand} rozpatrzy Twoje żądanie w ciągu <strong>30 dni</strong> od jego otrzymania.
          </p>

          <h2 style={s.h2}>Jakie dane usuwamy?</h2>
          <ul>
            <li>Imię, adres e-mail i numer telefonu podane przez formularz kontaktowy</li>
            <li>Treść wiadomości z wysłanych przez Ciebie zapytań</li>
            <li>Zapisane wyszukiwania i subskrypcje powiadomień push (jeśli dotyczy)</li>
            <li>Wszelkie inne dane powiązane z Twoim adresem e-mail w naszej bazie danych</li>
          </ul>

          <h2 style={s.h2}>Co jest przechowywane?</h2>
          <p>
            Niektóre dane musimy przechowywać ze względu na obowiązki ustawowe (np. obowiązki podatkowe przy
            faktycznie zrealizowanych transakcjach zakupu). Takie dane są anonimizowane po upływie ustawowego okresu
            przechowywania.
          </p>

          <h2 style={s.h2}>Złóż żądanie</h2>
          <p>Wypełnij poniższy formularz lub wyślij e-mail na <a href={`mailto:${company.email}?subject=Żądanie usunięcia danych`}>{company.email}</a>.</p>

          <form onSubmit={submit} style={styles.form} data-testid="data-deletion-form">
            <label style={styles.label}>
              Twój adres e-mail (wymagane)
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ty@email.com"
              style={styles.input}
              disabled={status === 'working' || status === 'success'}
              data-testid="data-deletion-email"
            />
            <p style={styles.helper}>
              Znajdziemy wszystkie rekordy powiązane z tym adresem e-mail i usuniemy je w ciągu 30 dni.
            </p>

            <label style={styles.label}>
              Dodatkowe informacje (opcjonalnie)
            </label>
            <textarea
              rows={4}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Na przykład, które konkretne dane chcesz usunąć…"
              style={styles.textarea}
              disabled={status === 'working' || status === 'success'}
              data-testid="data-deletion-details"
            />

            {status === 'success' && (
              <div style={styles.success} data-testid="data-deletion-success">
                ✓ Żądanie otrzymane. W ciągu 30 dni otrzymasz potwierdzenie e-mailem.
              </div>
            )}
            {status === 'error' && (
              <div style={styles.error} data-testid="data-deletion-error">
                Coś poszło nie tak. Jeśli problem się powtarza, wyślij żądanie e-mailem.
              </div>
            )}

            <button
              type="submit"
              style={styles.submit}
              disabled={status === 'working' || status === 'success'}
              data-testid="data-deletion-submit"
            >
              {status === 'working'
                ? 'Przetwarzanie…'
                : status === 'success'
                  ? 'Wysłano'
                  : 'Złóż żądanie'}
            </button>
          </form>
        </article>
      </div>
    </div>
  );
};

const styles = {
  form: { marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  label: { fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#8C8B88', marginTop: '0.6rem' },
  input: { padding: '0.75rem 0.9rem', border: '1px solid #EAE5DD', fontFamily: 'inherit', fontSize: '0.95rem', color: '#1A1918', backgroundColor: 'white', outline: 'none' },
  textarea: { padding: '0.75rem 0.9rem', border: '1px solid #EAE5DD', fontFamily: 'inherit', fontSize: '0.95rem', color: '#1A1918', backgroundColor: 'white', outline: 'none', resize: 'vertical' },
  helper: { fontSize: '0.75rem', color: '#8C8B88', margin: '0.15rem 0 0.4rem' },
  submit: { marginTop: '1rem', padding: '0.85rem 1.5rem', backgroundColor: '#1A1918', color: '#C5A059', border: 'none', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', alignSelf: 'flex-start', fontFamily: 'inherit' },
  success: { marginTop: '0.75rem', padding: '0.7rem 0.9rem', backgroundColor: '#E5F1E7', color: '#1F5D2E', border: '1px solid #B8D9BF', fontSize: '0.88rem' },
  error: { marginTop: '0.75rem', padding: '0.7rem 0.9rem', backgroundColor: '#F7E5E2', color: '#8A2A1F', border: '1px solid #E9C0B8', fontSize: '0.88rem' },
};

export default DataDeletion;
